import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Stack,
} from '@mui/material';
import { huHU } from '@mui/x-data-grid/locales';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TableChartIcon from '@mui/icons-material/TableChart';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';

import FileUploadDialogUI from './FileUploadDialogUI';
import FolderTemplateImportDialog from '../FolderTemplate/FolderTemplateImportDialog';
import { CheckboxUI, ColorCircleUi, ColorPaletteUI, TooltipUI } from '../Interface';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import FilePreviewDialog from './FilePreviewDialog';
import { imageExtensions } from '../../config/fileTab';
import { getAttachmentUrl, getFileExtension, isImageFile, isPreviewable } from '../../utils/fileTabHelper';

const rootFolder = { key: 'root', name: 'Főkönyvtár', type: 'root', children: [] };

const defFolderColor = '#ed6c02';

const createFolderTree = (savedFolders, newFolders) => {
  const root = { ...rootFolder, children: [] };
  const savedFolderMap = new Map();
  const newFolderMap = new Map();

  savedFolders.forEach((folder) => {
    savedFolderMap.set(folder.id, {
      ...folder,
      key: `folder-${folder.id}`,
      type: 'saved',
      children: [],
    });
  });

  newFolders.forEach((folder) => {
    newFolderMap.set(folder.clientId, {
      ...folder,
      key: `client-${folder.clientId}`,
      type: 'new',
      children: [],
    });
  });

  savedFolderMap.forEach((folder) => {
    const parent = savedFolderMap.get(folder.parentId) ?? root;
    parent.children.push(folder);
  });

  newFolderMap.forEach((folder) => {
    const parent = folder.parentId ? savedFolderMap.get(folder.parentId) : newFolderMap.get(folder.parentClientId);
    (parent ?? root).children.push(folder);
  });

  return root;
};

const findFolderPath = (folder, targetKey, path = []) => {
  const currentPath = [...path, folder];
  if (folder.key === targetKey) return currentPath;

  for (const child of folder.children) {
    const result = findFolderPath(child, targetKey, currentPath);
    if (result) return result;
  }

  return null;
};

const formatFileSize = (size) => {
  if (typeof size !== 'number') return '–';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatModifiedAt = (value) => {
  if (!value) return '–';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '–';

  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

const FileTypeIcon = ({ file }) => {
  const extension = getFileExtension(file);

  if (extension === '.pdf') return <PictureAsPdfIcon color="error" fontSize="small" />;
  if (['.doc', '.docx', '.odt', '.rtf'].includes(extension)) {
    return <DescriptionIcon color="primary" fontSize="small" />;
  }
  if (['.xls', '.xlsx', '.ods', '.csv'].includes(extension)) {
    return <TableChartIcon color="success" fontSize="small" />;
  }
  if (['.ppt', '.pptx', '.odp'].includes(extension)) {
    return <SlideshowIcon color="warning" fontSize="small" />;
  }
  if (imageExtensions.includes(extension)) {
    return <ImageIcon color="secondary" fontSize="small" />;
  }
  if (['.zip', '.rar', '.7z', '.tar', '.gz'].includes(extension)) {
    return <FolderZipIcon color="warning" fontSize="small" />;
  }
  if (['.js', '.jsx', '.ts', '.tsx', '.json', '.html', '.css', '.xml'].includes(extension)) {
    return <CodeIcon color="info" fontSize="small" />;
  }
  if (['.txt', '.md', '.log'].includes(extension)) {
    return <TextSnippetIcon color="action" fontSize="small" />;
  }
  if (['.mp3', '.wav', '.ogg', '.flac'].includes(extension)) {
    return <AudioFileIcon color="info" fontSize="small" />;
  }
  if (['.mp4', '.mov', '.avi', '.mkv', '.webm'].includes(extension)) {
    return <VideoFileIcon color="secondary" fontSize="small" />;
  }

  return <InsertDriveFileOutlinedIcon color="action" fontSize="small" />;
};

const FolderTreeItem = ({
  folder,
  selectedFolderKey,
  onSelect,
  getDraggedFile,
  dropTargetKey,
  onDragEnterFolder,
  onDragLeaveFolder,
  onFileDrop,
  level = 0,
}) => {
  const hasChildren = folder.children.length > 0;
  const [open, setOpen] = useState(folder.type === 'root');
  const dragEnterCount = useRef(0);

  const getAcceptedDraggedFile = () => {
    if (folder.readOnly) return null;
    const draggedFile = getDraggedFile();
    const draggedFiles = Array.isArray(draggedFile) ? draggedFile : [draggedFile];
    return draggedFile && (draggedFiles.every((file) => file instanceof File) || folder.type !== 'new')
      ? draggedFile
      : null;
  };

  return (
    <Box>
      <Box
        onDragEnter={(event) => {
          if (!getAcceptedDraggedFile()) return;
          event.preventDefault();
          event.stopPropagation();
          dragEnterCount.current += 1;
          if (dragEnterCount.current === 1) onDragEnterFolder(folder.key);
        }}
        onDragOver={(event) => {
          if (!getAcceptedDraggedFile()) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = 'move';
        }}
        onDragLeave={(event) => {
          if (!getAcceptedDraggedFile()) return;
          event.stopPropagation();
          dragEnterCount.current = Math.max(0, dragEnterCount.current - 1);
          if (dragEnterCount.current === 0) onDragLeaveFolder(folder.key);
        }}
        onDrop={(event) => {
          const draggedFile = getAcceptedDraggedFile();
          if (!draggedFile) return;
          event.preventDefault();
          event.stopPropagation();
          dragEnterCount.current = 0;
          onFileDrop(draggedFile, folder);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 36,
          pl: level * 2,
          pr: 1,
          borderRadius: 1,
          bgcolor:
            dropTargetKey === folder.key
              ? 'primary.light'
              : selectedFolderKey === folder.key
                ? 'action.selected'
                : 'transparent',
          outline: dropTargetKey === folder.key ? 2 : 0,
          outlineColor: 'primary.main',
          '&:hover': { bgcolor: dropTargetKey === folder.key ? 'primary.light' : 'action.hover' },
        }}
      >
        <IconButton
          size="small"
          aria-label={open ? 'Mappa összecsukása' : 'Mappa kibontása'}
          disabled={!hasChildren}
          onClick={() => setOpen((previousOpen) => !previousOpen)}
          sx={{ mr: 0.25, visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Button
          color="inherit"
          onClick={() => onSelect(folder.key)}
          startIcon={
            <Stack direction="row" spacing={1}>
              {folder.protected && (
                <TooltipUI title="Védett mappa">
                  <LockIcon fontSize="small" color="error" />
                </TooltipUI>
              )}
              <FolderIcon sx={{ color: folder.color ?? '#ed6c02' }} fontSize="small" />
            </Stack>
          }
          sx={{
            minWidth: 0,
            flex: 1,
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: selectedFolderKey === folder.key ? 600 : 400,
          }}
        >
          <Typography variant="body2" noWrap>
            {folder.name}
          </Typography>
        </Button>
      </Box>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {folder.children.map((child) => (
            <FolderTreeItem
              key={child.key}
              folder={child}
              selectedFolderKey={selectedFolderKey}
              onSelect={onSelect}
              getDraggedFile={getDraggedFile}
              dropTargetKey={dropTargetKey}
              onDragEnterFolder={onDragEnterFolder}
              onDragLeaveFolder={onDragLeaveFolder}
              onFileDrop={onFileDrop}
              level={level + 1}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const FolderColorPicker = ({ folderColor, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography>Mappa színe:</Typography>

      <Box>
        <ColorCircleUi color={folderColor} size={20} onClick={(event) => setAnchorEl(event.currentTarget)} />
      </Box>

      <ColorPaletteUI
        anchorEl={anchorEl}
        handlePopoverClose={() => setAnchorEl(null)}
        onClick={(color) => {
          onChange(color);
          setAnchorEl(null);
        }}
      />
    </Stack>
  );
};

const FolderOptions = ({ color, onColorChange, isProtected, onProtectedChange }) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
      <FolderColorPicker folderColor={color} onChange={onColorChange} />

      <CheckboxUI
        sx={{ m: 0, p: 0, pr: 0.5 }}
        label="Védett"
        checked={isProtected}
        onChange={(e) => onProtectedChange(e.target.checked)}
      />
    </Stack>
  );
};

const FileTabV2 = ({
  handleUpload,
  handleCreateFolder,
  handleDelete,
  handleDeleteFiles,
  handleDeleteFolder,
  handleMoveFile,
  handleMoveFiles,
  handleEditFolder,
  moveDisabled = false,
  disabled,
  savedFiles = [],
  savedFolders = [],
  newFolders = [],
  fileFolderAssignments = [],
  handleImportFolder,
  ...props
}) => {
  const [selectedFolderKey, setSelectedFolderKey] = useState('root');
  const [uploadDialog, setUploadDialog] = useState(false);
  const [folderDialog, setFolderDialog] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState(defFolderColor);
  const [folderProtected, setFolderProtected] = useState(false);
  const [folderNameError, setFolderNameError] = useState('');
  const [dropTargetKey, setDropTargetKey] = useState(null);
  const [fileMenuAnchor, setFileMenuAnchor] = useState(null);
  const [fileMenuRow, setFileMenuRow] = useState(null);
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [moveDialog, setMoveDialog] = useState(false);
  const [moveTargetFolderKey, setMoveTargetFolderKey] = useState('root');
  const [deleteFilesDialog, setDeleteFilesDialog] = useState(false);
  const [editFolderDialog, setEditFolderDialog] = useState(false);
  const [deleteFolderDialog, setDeleteFolderDialog] = useState(false);
  const [importFolderDialog, setImportFolderDialog] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [editedFolderName, setEditedFolderName] = useState('');
  const [editedFolderNameError, setEditedFolderNameError] = useState('');
  const [editedFolderColor, setEditedFolderColor] = useState(defFolderColor);
  const [editedFolderProtected, setEditedFolderProtected] = useState(false);
  const [folderCreateMenuAnchor, setFolderCreateMenuAnchor] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(null);
  const draggedFileRef = useRef(null);

  const folderTree = useMemo(() => createFolderTree(savedFolders, newFolders), [savedFolders, newFolders]);
  const selectedPath = useMemo(
    () => findFolderPath(folderTree, selectedFolderKey) ?? [folderTree],
    [folderTree, selectedFolderKey]
  );
  const selectedFolder = selectedPath[selectedPath.length - 1];

  const rows = useMemo(() => {
    const newFiles = savedFiles.filter((file) => file instanceof File);
    const newFileIndexes = new Map(newFiles.map((file, index) => [file, index]));
    const folderRows = selectedFolder.children.map((folder) => ({
      ...folder,
      rowId: folder.key,
      rowType: 'folder',
      typeName: 'Mappa',
      modifiedAt: formatModifiedAt(folder.updatedAt),
      sortModifiedAt: folder.updatedAt ? new Date(folder.updatedAt).getTime() : 0,
      size: '–',
      sortSize: -1,
    }));

    const fileRows = savedFiles
      .filter((file) => {
        if (!(file instanceof File)) {
          return selectedFolder.type === 'root' ? file.folderId == null : file.folderId === selectedFolder.id;
        }

        const assignment = fileFolderAssignments.find(
          (fileFolderAssignment) => fileFolderAssignment.fileIndex === newFileIndexes.get(file)
        );

        if (selectedFolder.type === 'saved') return assignment?.folderId === selectedFolder.id;
        if (selectedFolder.type === 'new') return assignment?.folderClientId === selectedFolder.clientId;
        return !assignment || (assignment.folderId == null && assignment.folderClientId == null);
      })
      .map((file, index) => ({
        ...file,
        file,
        name: file.name,
        rowId: `file-${file.id ?? file.path ?? index}-${file.name}`,
        rowType: 'file',
        typeName: getFileExtension(file).slice(1).toUpperCase() || 'Fájl',
        modifiedAt: formatModifiedAt(file.updatedAt ?? (file instanceof File ? file.lastModified : null)),
        sortModifiedAt: file.updatedAt
          ? new Date(file.updatedAt).getTime()
          : file instanceof File
            ? file.lastModified
            : 0,
        size: formatFileSize(file.sizeBytes ?? file.size),
        sortSize: typeof (file.sizeBytes ?? file.size) === 'number' ? file.sizeBytes ?? file.size : -1,
      }));

    return [...folderRows, ...fileRows];
  }, [fileFolderAssignments, savedFiles, selectedFolder]);

  const sortedRows = useMemo(() => {
    const activeSort = sortModel[0] ?? { field: 'name', sort: 'asc' };
    const directionMultiplier = activeSort.sort === 'desc' ? -1 : 1;

    return [...rows].sort((firstRow, secondRow) => {
      if (firstRow.rowType !== secondRow.rowType) {
        return firstRow.rowType === 'folder' ? -1 : 1;
      }

      let comparison;

      if (activeSort.field === 'modifiedAt') {
        comparison = firstRow.sortModifiedAt - secondRow.sortModifiedAt;
      } else if (activeSort.field === 'size') {
        comparison = firstRow.sortSize - secondRow.sortSize;
      } else {
        const firstValue = activeSort.field === 'typeName' ? firstRow.typeName : firstRow.name;
        const secondValue = activeSort.field === 'typeName' ? secondRow.typeName : secondRow.name;
        comparison = firstValue.localeCompare(secondValue, 'hu', { numeric: true, sensitivity: 'base' });
      }

      return comparison * directionMultiplier;
    });
  }, [rows, sortModel]);

  const imageFiles = sortedRows.filter((row) => row.rowType === 'file' && isImageFile(row.file)).map((row) => row.file);

  const selectedFileRows = useMemo(
    () => rows.filter((row) => row.rowType === 'file' && rowSelectionModel.includes(row.rowId)),
    [rowSelectionModel, rows]
  );
  const moveTargetPath = useMemo(
    () => findFolderPath(folderTree, moveTargetFolderKey) ?? [folderTree],
    [folderTree, moveTargetFolderKey]
  );
  const moveTargetFolder = moveTargetPath[moveTargetPath.length - 1];
  const canMoveSelectionToTarget =
    !moveTargetFolder.readOnly &&
    (moveTargetFolder.type !== 'new' || selectedFileRows.every((row) => row.file instanceof File));

  useEffect(() => {
    setRowSelectionModel([]);
  }, [selectedFolderKey]);

  const closeFolderDialog = () => {
    setFolderDialog(false);
    setFolderName('');
    setFolderColor(defFolderColor);
    setFolderProtected(false);
    setFolderNameError('');
  };

  const createFolder = async () => {
    const name = folderName.trim();
    if (!name) {
      setFolderNameError('A mappa neve kötelező');
      return;
    }

    const duplicateName = selectedFolder.children.some(
      (folder) => folder.name.localeCompare(name, 'hu', { sensitivity: 'base' }) === 0
    );
    if (duplicateName) {
      setFolderNameError('Ebben a mappában már létezik ilyen nevű mappa');
      return;
    }

    try {
      await handleCreateFolder(name, folderColor, folderProtected, selectedFolder);
      closeFolderDialog();
    } catch {
      // A globális request interceptor megjeleníti a backend hibaüzenetét.
    }
  };

  const importFolders = async (folderTemplateItems) => {
    const rootTemplateFolders = folderTemplateItems.filter((templateFolder) => templateFolder.parentId == null);
    const duplicateFolder = rootTemplateFolders.find((templateFolder) =>
      selectedFolder.children.some(
        (folder) =>
          folder.name.localeCompare(templateFolder.name, 'hu', {
            sensitivity: 'base',
          }) === 0
      )
    );

    if (duplicateFolder) {
      addNotification({
        content: `A(z) "${duplicateFolder.name}" mappa már létezik ebben a mappában.`,
        type: NotificationType.WARNING,
      });
      return;
    }

    await handleImportFolder(folderTemplateItems, selectedFolder);
    setImportFolderDialog(false);
  };

  const dropFile = (file, targetFolder) => {
    const moveRequest = Array.isArray(file)
      ? handleMoveFiles?.(file, targetFolder)
      : handleMoveFile?.(file, targetFolder);
    moveRequest?.catch?.(() => {
      // A globális request interceptor megjeleníti a backend hibaüzenetét.
    });
    draggedFileRef.current = null;
    setDropTargetKey(null);
  };

  const showDragIcon = (row) =>
    Boolean(handleMoveFile) && !moveDisabled && row.rowType === 'file' && !row.file?.readOnly;

  const canDragFile = (row) => showDragIcon(row) && !disabled;

  const startDraggingFile = (event, row) => {
    if (!canDragFile(row)) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', row.rowId);
    draggedFileRef.current =
      rowSelectionModel.includes(row.rowId) && selectedFileRows.length > 1
        ? selectedFileRows.map((selectedRow) => selectedRow.file)
        : row.file;
  };

  const stopDraggingFile = () => {
    draggedFileRef.current = null;
    setDropTargetKey(null);
  };

  const closeFileMenu = () => {
    setFileMenuAnchor(null);
    setFileMenuRow(null);
  };

  const closeFolderCreateMenu = () => {
    setFolderCreateMenuAnchor(null);
  };

  const triggerDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const downloadFile = async (file) => {
    const filename = file?.name || 'download';

    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      triggerDownload(url, filename);
      URL.revokeObjectURL(url);
      return;
    }

    if (file?.path) {
      const url = getAttachmentUrl(file.path);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerDownload(blobUrl, filename);
      URL.revokeObjectURL(blobUrl);
    }
  };

  const previewFile = (file) => {
    const index = imageFiles.findIndex((item) => item === file);

    if (index !== -1) {
      setPreviewIndex(index);
      return;
    }

    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }

    if (file?.path) {
      window.open(getAttachmentUrl(file.path), '_blank');
    }
  };

  const moveSelectedFiles = async () => {
    if (!handleMoveFiles || selectedFileRows.length === 0) return;

    try {
      await handleMoveFiles(
        selectedFileRows.map((row) => row.file),
        moveTargetFolder
      );
      setMoveDialog(false);
      setRowSelectionModel([]);
    } catch {
      // A globális request interceptor megjeleníti a backend hibaüzenetét.
    }
  };

  const deleteSelectedFiles = async () => {
    if (!handleDeleteFiles || selectedFileRows.length === 0) return;

    try {
      await handleDeleteFiles(selectedFileRows.map((row) => row.file));
      setDeleteFilesDialog(false);
      setRowSelectionModel([]);
    } catch {
      // A globális request interceptor megjeleníti a backend hibaüzenetét.
    }
  };

  const openEditFolderDialog = (folder) => {
    setFolderToEdit(folder);
    setEditedFolderName(folder.name);
    setEditedFolderColor(folder.color);
    setEditedFolderProtected(folder.protected);
    setEditedFolderNameError('');
    setEditFolderDialog(true);
  };

  const editFolder = async () => {
    const name = editedFolderName.trim();
    if (!name) {
      setEditedFolderNameError('A mappa neve kötelező');
      return;
    }

    const duplicateName = selectedFolder.children.some(
      (folder) =>
        folder.key !== folderToEdit.key && folder.name.localeCompare(name, 'hu', { sensitivity: 'base' }) === 0
    );
    if (duplicateName) {
      setEditedFolderNameError('Ebben a mappában már létezik ilyen nevű mappa');
      return;
    }

    try {
      await handleEditFolder(folderToEdit, name, editedFolderColor, editedFolderProtected);
      setEditFolderDialog(false);
      setFolderToEdit(null);
    } catch {
      // A globális request interceptor megjeleníti a backend hibaüzenetét.
    }
  };

  const deleteFolder = async () => {
    try {
      await handleDeleteFolder(folderToEdit);
      setDeleteFolderDialog(false);
      setFolderToEdit(null);
    } catch {
      // A globális request interceptor megjeleníti a backend hibaüzenetét.
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Név',
      minWidth: 180,
      flex: 1,
      renderCell: ({ row }) => (
        <Box
          draggable={canDragFile(row)}
          onDragStart={(event) => startDraggingFile(event, row)}
          onDragEnd={stopDraggingFile}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            height: '100%',
            minWidth: 0,
            cursor: canDragFile(row) ? 'grab' : row.rowType === 'folder' ? 'pointer' : 'default',
            userSelect: 'none',
          }}
        >
          {showDragIcon(row) && (
            <DragIndicatorIcon
              fontSize="small"
              sx={{ flexShrink: 0, color: canDragFile(row) ? 'action.active' : 'action.disabled' }}
            />
          )}
          {row.rowType === 'folder' ? (
            <>
              {row.protected && (
                <TooltipUI title="Védett mappa">
                  <LockIcon fontSize="small" color="error" />
                </TooltipUI>
              )}

              <FolderIcon sx={{ color: row.color }} fontSize="small" />
            </>
          ) : (
            <FileTypeIcon file={row.file} />
          )}
          <Typography variant="body2" noWrap>
            {row.name}
          </Typography>
        </Box>
      ),
    },
    { field: 'typeName', headerName: 'Típus', width: 140, minWidth: 90 },
    { field: 'modifiedAt', headerName: 'Módosítva', width: 190, minWidth: 120 },
    { field: 'size', headerName: 'Méret', width: 110, minWidth: 80 },
    {
      field: 'actions',
      headerName: '',
      width: 52,
      minWidth: 52,
      maxWidth: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.rowType === 'file' ||
        (row.rowType === 'folder' && !row.readOnly && (handleEditFolder || handleDeleteFolder)) ? (
          <IconButton
            size="small"
            aria-label={`${row.name} műveletei`}
            onClick={(event) => {
              event.stopPropagation();
              setFileMenuAnchor(event.currentTarget);
              setFileMenuRow(row);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 2,
        minHeight: 480,
        width: '100%',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: { xs: '100%', md: 280 },
          flex: { xs: '1 1 100%', md: '0 0 280px' },
          maxHeight: { xs: 240, md: 'none' },
          p: 1.5,
          overflow: 'auto',
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
          Mappák
        </Typography>
        <FolderTreeItem
          folder={folderTree}
          selectedFolderKey={selectedFolderKey}
          onSelect={setSelectedFolderKey}
          getDraggedFile={() => draggedFileRef.current}
          dropTargetKey={dropTargetKey}
          onDragEnterFolder={setDropTargetKey}
          onDragLeaveFolder={(folderKey) => {
            setDropTargetKey((currentKey) => (currentKey === folderKey ? null : currentKey));
          }}
          onFileDrop={dropFile}
        />
      </Paper>

      <Paper
        variant="outlined"
        sx={{ width: '100%', minWidth: 0, flex: { xs: '1 1 100%', md: '1 1 500px' }, overflow: 'hidden' }}
      >
        <Box
          sx={{
            minHeight: 58,
            px: 2,
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Breadcrumbs
            aria-label="Aktuális mappa"
            separator={<ChevronRightIcon fontSize="small" />}
            sx={{ minWidth: 0, flex: '1 1 240px' }}
          >
            {selectedPath.map((folder, index) =>
              index === selectedPath.length - 1 ? (
                <Typography key={folder.key} color="primary" variant="body2" fontWeight={600}>
                  {folder.name}
                </Typography>
              ) : (
                <Link
                  key={folder.key}
                  component="button"
                  type="button"
                  underline="hover"
                  color="text.secondary"
                  variant="body2"
                  onClick={() => setSelectedFolderKey(folder.key)}
                >
                  {folder.name}
                </Link>
              )
            )}
          </Breadcrumbs>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
            {selectedFileRows.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
                {selectedFileRows.length} fájl kijelölve
              </Typography>
            )}
            {selectedFileRows.length > 0 && handleMoveFiles && (
              <Button
                variant="outlined"
                startIcon={<DriveFileMoveOutlinedIcon />}
                disabled={disabled || moveDisabled}
                onClick={() => {
                  setMoveTargetFolderKey('root');
                  setMoveDialog(true);
                }}
                sx={{ textTransform: 'none' }}
              >
                Áthelyezés
              </Button>
            )}
            {selectedFileRows.length > 0 && handleDeleteFiles && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                disabled={disabled}
                onClick={() => setDeleteFilesDialog(true)}
                sx={{ textTransform: 'none' }}
              >
                Törlés
              </Button>
            )}
            {handleCreateFolder && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CreateNewFolderOutlinedIcon />}
                  disabled={disabled || selectedFolder.readOnly}
                  onClick={(event) => setFolderCreateMenuAnchor(event.currentTarget)}
                  sx={{ textTransform: 'none' }}
                >
                  Új mappa
                </Button>

                <Menu
                  anchorEl={folderCreateMenuAnchor}
                  open={Boolean(folderCreateMenuAnchor)}
                  onClose={closeFolderCreateMenu}
                >
                  <MenuItem
                    onClick={() => {
                      closeFolderCreateMenu();
                      setFolderDialog(true);
                    }}
                  >
                    Egyéni
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      closeFolderCreateMenu();
                      setImportFolderDialog(true);
                    }}
                  >
                    Sablon
                  </MenuItem>
                </Menu>
              </>
            )}
            {handleUpload && (
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                disabled={disabled || selectedFolder.readOnly}
                onClick={() => setUploadDialog(true)}
                sx={{ textTransform: 'none' }}
              >
                Feltöltés
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ height: 410, width: '100%' }}>
          <DataGridPremium
            rows={sortedRows}
            columns={columns}
            getRowId={(row) => row.rowId}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            isRowSelectable={({ row }) => row.rowType === 'file' && !row.file?.readOnly}
            disableRowSelectionOnClick
            hideFooter
            localeText={huHU.components.MuiDataGrid.defaultProps.localeText}
            getRowClassName={({ row }) => (row.rowType === 'folder' ? 'folder-row' : '')}
            onRowDoubleClick={({ row }) => {
              if (row.rowType === 'folder') {
                setSelectedFolderKey(row.key);
              } else if (isPreviewable(row.file)) {
                previewFile(row.file);
              } else {
                downloadFile(row.file);
              }
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">A mappa üres</Typography>
                </Box>
              ),
            }}
            sx={{
              border: 0,
              '& .MuiDataGrid-columnSeparator': { visibility: 'visible' },
              '& .MuiDataGrid-row': { cursor: 'default' },
              '& .MuiDataGrid-row.folder-row': { cursor: 'pointer' },
            }}
          />
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
        >
          {rows.length} elem
        </Typography>
      </Paper>

      <Menu anchorEl={fileMenuAnchor} open={Boolean(fileMenuAnchor)} onClose={closeFileMenu}>
        {fileMenuRow?.rowType === 'folder' && !fileMenuRow.readOnly && handleEditFolder && (
          <MenuItem
            disabled={disabled}
            onClick={() => {
              openEditFolderDialog(fileMenuRow);
              closeFileMenu();
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Szerkesztés
          </MenuItem>
        )}
        {fileMenuRow?.rowType === 'folder' && !fileMenuRow.readOnly && handleDeleteFolder && (
          <MenuItem
            disabled={disabled}
            onClick={() => {
              setFolderToEdit(fileMenuRow);
              setDeleteFolderDialog(true);
              closeFileMenu();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Törlés
          </MenuItem>
        )}
        {fileMenuRow?.rowType === 'file' && isPreviewable(fileMenuRow.file) && (
          <MenuItem
            onClick={() => {
              previewFile(fileMenuRow.file);
              closeFileMenu();
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            Előnézet
          </MenuItem>
        )}
        {fileMenuRow?.rowType === 'file' && (
          <MenuItem
            disabled={!fileMenuRow?.file?.path && !(fileMenuRow?.file instanceof File)}
            onClick={() => {
              downloadFile(fileMenuRow.file);
              closeFileMenu();
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            Letöltés
          </MenuItem>
        )}
        {fileMenuRow?.rowType === 'file' && handleDelete && (
          <MenuItem
            disabled={
              disabled ||
              fileMenuRow?.file?.readOnly ||
              (fileMenuRow?.file?.deletable !== undefined && fileMenuRow.file.deletable !== true)
            }
            onClick={() => {
              handleDelete(fileMenuRow.file);
              closeFileMenu();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Törlés
          </MenuItem>
        )}
      </Menu>

      {handleUpload && (
        <FileUploadDialogUI
          open={uploadDialog}
          handleClose={() => setUploadDialog(false)}
          handleUpload={(files) => {
            handleUpload(files, selectedFolder)?.catch?.(() => {
              // A globális request interceptor megjeleníti a backend hibaüzenetét.
            });
            setUploadDialog(false);
          }}
          {...props}
        />
      )}

      <Dialog open={folderDialog} onClose={closeFolderDialog} fullWidth maxWidth="xs">
        <DialogTitle>Új mappa</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Mappa neve"
            value={folderName}
            error={Boolean(folderNameError)}
            helperText={folderNameError}
            onChange={(event) => {
              setFolderName(event.target.value);
              setFolderNameError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') createFolder();
            }}
          />
          <FolderOptions
            color={folderColor}
            onColorChange={setFolderColor}
            isProtected={folderProtected}
            onProtectedChange={setFolderProtected}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFolderDialog}>Mégse</Button>
          <Button variant="contained" onClick={createFolder}>
            Létrehozás
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveDialog} onClose={() => setMoveDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Fájlok áthelyezése</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Válassza ki a célmappát ({selectedFileRows.length} fájl)
          </Typography>
          <FolderTreeItem
            folder={folderTree}
            selectedFolderKey={moveTargetFolderKey}
            onSelect={setMoveTargetFolderKey}
            getDraggedFile={() => null}
            dropTargetKey={null}
            onDragEnterFolder={() => {}}
            onDragLeaveFolder={() => {}}
            onFileDrop={() => {}}
          />
          {!canMoveSelectionToTarget && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              Már mentett fájl csak mentett mappába vagy a projekt gyökerébe helyezhető át.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog(false)}>Mégse</Button>
          <Button variant="contained" disabled={!canMoveSelectionToTarget} onClick={moveSelectedFiles}>
            Áthelyezés
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteFilesDialog} onClose={() => setDeleteFilesDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Fájlok törlése</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>Biztosan törölni szeretné a kijelölt {selectedFileRows.length} fájlt?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFilesDialog(false)}>Mégse</Button>
          <Button color="error" variant="contained" onClick={deleteSelectedFiles}>
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editFolderDialog} onClose={() => setEditFolderDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Mappa szerkesztése</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Mappa neve"
            value={editedFolderName}
            error={Boolean(editedFolderNameError)}
            helperText={editedFolderNameError}
            onChange={(event) => {
              setEditedFolderName(event.target.value);
              setEditedFolderNameError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') editFolder();
            }}
          />
          <FolderOptions
            color={editedFolderColor}
            onColorChange={setEditedFolderColor}
            isProtected={editedFolderProtected}
            onProtectedChange={setEditedFolderProtected}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFolderDialog(false)}>Mégse</Button>
          <Button variant="contained" onClick={editFolder}>
            Mentés
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteFolderDialog} onClose={() => setDeleteFolderDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Mappa törlése</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Biztosan törölni szeretné a(z) „{folderToEdit?.name}” mappát és annak teljes tartalmát?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialog(false)}>Mégse</Button>
          <Button color="error" variant="contained" onClick={deleteFolder}>
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      <FolderTemplateImportDialog
        open={importFolderDialog}
        handleClose={() => setImportFolderDialog(false)}
        handleImportFolder={importFolders}
      />

      <FilePreviewDialog
        open={previewIndex !== null}
        files={imageFiles}
        selectedIndex={previewIndex ?? 0}
        handleClose={() => setPreviewIndex(null)}
      />
    </Box>
  );
};

export default FileTabV2;
